import React, { useEffect, PropsWithChildren } from "react"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { useSession } from "lib/auth/react"
import { EventAction, EventCategory } from "models/GoogleAnalytics"

/**
 * Provider that automatically detects successful magic link logins and fires GA events.
 *
 * Usage: Wrap your app root with this provider:
 * ```tsx
 * <LoginSuccessProvider>
 *   <App />
 * </LoginSuccessProvider>
 * ```
 *
 * How it works:
 * 1. Detects `login_success=true` URL parameter (added by magic link flow)
 * 2. Verifies user has a valid session
 * 3. Fires GA event when both conditions are met
 * 4. Cleans up URL parameters after event is sent
 */
export const LoginSuccessProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { data: session, error: sessionError } = useSession()

  useEffect(() => {
    // Check if this is a login success redirect
    const urlParams = new URLSearchParams(window.location.search)
    const isLoginSuccess = urlParams.get("login_success") === "true"

    if (!isLoginSuccess) {
      return
    }

    // Wait for session to load
    if (session === undefined) {
      // Still loading
      return
    }

    if (sessionError || !session) {
      // No valid session - this shouldn't happen with login_success=true
      // but clean up the parameter anyway
      cleanupUrlParameters()
      return
    }

    // We have both login_success=true and a valid session - fire GA event
    try {
      LogGaEvent({
        action: EventAction.loginSuccess,
        event_category: EventCategory.accountInteraction,
        event_label: "Magic Link Login Complete",
      })
    } catch (error) {
      // GA event failed, but don't break the app
      console.warn("Failed to send login success GA event:", error)
    }

    // Clean up URL parameters after event is sent
    cleanupUrlParameters()
  }, [session, sessionError])

  return <>{children}</>
}

/**
 * Removes login_success parameter from URL without causing a page reload
 */
function cleanupUrlParameters() {
  const url = new URL(window.location.href)
  url.searchParams.delete("login_success")

  // Use replaceState to update URL without reload
  window.history.replaceState({}, "", url.toString())
}